import { useEffect, useState } from "react";
import { BarChart3, Download, DatabaseBackup } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fetchBlob, saveBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SERVICE, PAY_STATUS, todayISO } from "@/lib/util";
import { Loader, Money, Section, Field } from "@/components/common";
import { PageTitle, Table, Td, FileInput } from "@/pages/admin/ui";
import { DateRange } from "@/pages/admin/Trips";

const EXPORTS = [["trips", "النقلات"], ["invoices", "الفواتير"], ["violations", "المخالفات"], ["fee_payments", "دفعات الرسوم"], ["drivers", "السائقون"], ["audit", "سجل النشاط"]];

function Backup() {
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const down = async () => {
    try { saveBlob(await fetchBlob("/backup"), `backup-${todayISO()}.json`); toast.success("تم تنزيل النسخة الاحتياطية"); } catch (e) { toast.error(errMsg(e)); }
  };
  const restore = async () => {
    const fd = new FormData();
    fd.append("confirm", confirm);
    fd.append("file", file);
    try { await api.post("/restore", fd); toast.success("تمت الاستعادة"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title={<span className="flex items-center gap-2"><DatabaseBackup className="w-5 h-5" />النسخ الاحتياطي والاستعادة (مدير النظام)</span>}>
      <p className="text-sm text-slate-500 mb-3">النسخة تشمل جميع السجلات (بدون الملفات نفسها؛ المرفقات محفوظة في التخزين الخاص ومرتبطة بالسجلات).</p>
      <button data-testid="backup-download-btn" onClick={down} className="btn btn-md btn-dark mb-5"><Download className="w-5 h-5" />تنزيل نسخة احتياطية الآن</button>
      <div className="rounded-xl border-2 border-red-200 p-4 space-y-3">
        <b className="text-red-700">استعادة نسخة (تستبدل جميع البيانات الحالية)</b>
        <FileInput testid="restore-file" accept="application/json" onChange={setFile} />
        <Field label="اكتب كلمة «استعادة» للتأكيد"><input data-testid="restore-confirm" className="field field-sm" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
        <button data-testid="restore-btn" disabled={!file || confirm !== "استعادة"} onClick={restore} className="btn btn-md btn-red">استعادة</button>
      </div>
    </Section>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const [from, setFrom] = useState(todayISO().slice(0, 8) + "01");
  const [to, setTo] = useState(todayISO());
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/reports/summary", { params: { date_from: from, date_to: to } }).then((x) => setR(x.data)).catch((e) => toast.error(errMsg(e))); }, [from, to]);
  const exp = async (k) => {
    try { saveBlob(await fetchBlob(`/export/${k}?date_from=${from}&date_to=${to}`), `${k}-${from}-${to}.csv`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5">
      <PageTitle title="التقارير والتصدير" icon={BarChart3}><DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} /></PageTitle>
      {!r ? <Loader /> : (
        <div className="grid lg:grid-cols-2 gap-5">
          <Section title="النقلات حسب الخدمة">
            <Table head={["الخدمة", "العدد", "القيمة", "المحصل"]} testid="report-service">
              {r.by_service.map((x) => <tr key={x._id}><Td>{SERVICE[x._id]}</Td><Td>{x.count}</Td><Td><Money fils={x.value} /></Td><Td><Money fils={x.paid} /></Td></tr>)}
            </Table>
          </Section>
          <Section title="حسب حالة الدفع">
            <Table head={["الحالة", "العدد", "القيمة"]} testid="report-status">
              {r.by_payment_status.map((x) => <tr key={x._id}><Td>{PAY_STATUS[x._id]}</Td><Td>{x.count}</Td><Td><Money fils={x.value} /></Td></tr>)}
            </Table>
            <div className="mt-4 space-y-1 text-sm">
              <div>المخالفات: <b>{r.violations}</b> بمبلغ <Money fils={r.violations_fils} /></div>
              <div>الرسوم المسجلة: <Money fils={r.fees_fils} /> • دفعات الرسوم: <Money fils={r.fee_payments_fils} /></div>
            </div>
          </Section>
          <div className="lg:col-span-2"><Section title="أداء السائقين">
            <Table head={["السائق", "عدد النقلات", "القيمة", "المحصل"]} testid="report-drivers">
              {r.by_driver.map((x) => <tr key={x._id}><Td>{x.name} <span className="ltr text-slate-500">{x.serial}</span></Td><Td>{x.count}</Td><Td><Money fils={x.value} /></Td><Td><Money fils={x.paid} /></Td></tr>)}
            </Table>
          </Section></div>
        </div>
      )}
      <Section title="تصدير البيانات (CSV يفتح في Excel)">
        <div className="flex flex-wrap gap-2">{EXPORTS.map(([k, l]) => <button key={k} data-testid={`export-${k}`} onClick={() => exp(k)} className="btn btn-md btn-ghost"><Download className="w-5 h-5" />{l}</button>)}</div>
      </Section>
      {user.role === "admin" && <Backup />}
    </div>
  );
}
